/**
 * Start / pause / resume / stop handlers for the Public Transcription page.
 *
 * Parallels usePublicRecordingHandlers but swaps the audio-upload tail of
 * stopRecording for: (a) collecting the in-memory transcript from the
 * Deepgram service, and (b) posting a `transcriptionComplete` message to the
 * parent iframe owner with the full transcript and detected commands.
 */

import type {
  PublicTranscriptionService,
  TranscriptionResult,
} from '@/lib/public-transcription-service';
import { useCallback } from 'react';
import { useToast } from './use-toast';

interface UsePublicTranscriptionHandlersParams {
  serviceRef: React.MutableRefObject<PublicTranscriptionService | null>;
  scribeJobId: string | null;
  sectionTag: string;
  setIsRecording: (value: boolean) => void;
  setIsPaused: (value: boolean) => void;
  setIsUploading: (value: boolean) => void;
  setIsProcessing: (value: boolean) => void;
  setError: (value: string | null) => void;
}

export const usePublicTranscriptionHandlers = ({
  serviceRef,
  scribeJobId,
  sectionTag,
  setIsRecording,
  setIsPaused,
  setIsUploading,
  setIsProcessing,
  setError,
}: UsePublicTranscriptionHandlersParams) => {
  const { toast } = useToast();

  const handleStartRecording = useCallback(async () => {
    console.log('[PublicTranscription] Start button clicked');

    if (!scribeJobId) {
      const errorMsg =
        'Transcription session not initialized. Please refresh the page.';
      setError(errorMsg);
      toast({
        title: 'Not Ready',
        description: errorMsg,
        variant: 'destructive',
        duration: 2000,
      });
      return;
    }

    if (!serviceRef.current) {
      const errorMsg =
        'Transcription service not available. Please refresh the page.';
      setError(errorMsg);
      toast({
        title: 'Service Error',
        description: errorMsg,
        variant: 'destructive',
        duration: 2000,
      });
      return;
    }

    try {
      setError(null);
      await serviceRef.current.startRecording();
      setIsRecording(true);
      setIsPaused(false);
      console.log('[PublicTranscription] ✅ Transcription started');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicTranscription] Failed to start:', err);
      const isPermissionError =
        errorMessage.toLowerCase().includes('permission') ||
        errorMessage.toLowerCase().includes('notallowed') ||
        errorMessage.toLowerCase().includes('denied');
      const userErrorMsg = isPermissionError
        ? 'Microphone access denied. Please allow microphone permissions and try again.'
        : `Failed to start transcription: ${errorMessage}`;
      setError(userErrorMsg);
      toast({
        title: 'Transcription Failed',
        description: userErrorMsg,
        variant: 'destructive',
        duration: 2000,
      });
    }
  }, [scribeJobId, serviceRef, setError, setIsRecording, setIsPaused, toast]);

  const handlePauseRecording = useCallback(async () => {
    if (!serviceRef.current) return;
    try {
      await serviceRef.current.pauseRecording();
      setIsPaused(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicTranscription] Failed to pause:', err);
      toast({
        title: 'Pause Failed',
        description: `Failed to pause: ${errorMessage}`,
        variant: 'destructive',
        duration: 2000,
      });
    }
  }, [serviceRef, setIsPaused, toast]);

  const handleResumeRecording = useCallback(async () => {
    if (!serviceRef.current) return;
    try {
      await serviceRef.current.resumeRecording();
      setIsPaused(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicTranscription] Failed to resume:', err);
      toast({
        title: 'Resume Failed',
        description: `Failed to resume: ${errorMessage}`,
        variant: 'destructive',
        duration: 2000,
      });
    }
  }, [serviceRef, setIsPaused, toast]);

  const handleStopRecording = useCallback(async () => {
    if (!serviceRef.current) return;
    if (!scribeJobId) return;

    try {
      setIsRecording(false);
      setIsPaused(false);
      setIsUploading(true);

      const result: TranscriptionResult =
        await serviceRef.current.stopRecording();

      setIsUploading(false);
      setIsProcessing(false);

      // Notify the parent (EyePegasus / any embedding window) that the
      // transcription session ended and hand over the full transcript +
      // command log. Mirrors the audioUploadComplete contract of the
      // PublicRecording flow.
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'transcriptionComplete',
            payload: {
              fullTranscript: result.fullTranscript,
              segments: result.segments,
              commands: result.commands,
              durationSeconds: result.durationSeconds,
              scribeJobId,
              sectionTag,
              timestamp: new Date().toISOString(),
            },
            sectionTag,
          },
          '*'
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicTranscription] Failed to stop:', err);
      setIsUploading(false);
      setIsProcessing(false);
      toast({
        title: 'Stop Failed',
        description: `Failed to stop transcription: ${errorMessage}`,
        variant: 'destructive',
        duration: 2000,
      });
    }
  }, [
    scribeJobId,
    sectionTag,
    serviceRef,
    setIsRecording,
    setIsPaused,
    setIsUploading,
    setIsProcessing,
    toast,
  ]);

  return {
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  };
};
